import type { DailyLog, IsoDate, LogMap } from "../domain/types.js";

/**
 * 每日紀錄的樂觀更新。
 *
 * 打勾要立刻有反應，但寫入可能失敗——失敗時必須把畫面改回去，否則使用者
 * 會以為存好了。這段生命週期（樂觀寫入 → 送出 → 失敗回滾 → 回報）先前
 * 散在事件處理器裡，是全 app 風險最高的邏輯（回滾錯了就是資料遺失），
 * 卻只能靠瀏覽器測。
 *
 * 這裡把它收成一個 module：interface 只有四個成員，實作吸收了回滾、
 * 延遲儲存、併發保護與錯誤回報。
 */

/**
 * 寫入端。正式環境是 fetch，測試用 in-memory 的假實作。
 *
 * **save 必須回傳完整的資料列**，不是只有這次修改的欄位——store 會用它
 * 取代本地那一天的整筆紀錄，回傳不完整就會靜默弄丟其他欄位。
 * `PUT /api/daily-logs` 回傳的是 `SELECT *`，符合這個約定。
 */
export interface DailyLogWriter {
  save(date: IsoDate, patch: Partial<DailyLog>): Promise<DailyLog>;
}

export interface DailyLogStoreOptions {
  writer: DailyLogWriter;
  /** 資料有變、需要重繪。樂觀寫入與回滾都會觸發。 */
  onChange: () => void;
  /** 寫入失敗時回報給使用者。 */
  onError: (message: string) => void;
  /** 備註的延遲儲存間隔。每次按鍵都送會打爆 API。 */
  noteDebounceMs?: number;
}

export interface DailyLogStore {
  readonly logs: LogMap;
  /** 從 API 載入一整批，覆蓋現有內容。 */
  replaceAll(logs: LogMap): void;
  /** 打勾／取消打勾。立刻反映在 logs，失敗會自動回滾。 */
  toggle(date: IsoDate, field: keyof DailyLog, next: boolean): Promise<void>;
  /** 輸入備註。立刻反映在 logs，延遲後才送出。 */
  setNote(date: IsoDate, text: string): void;
  /** 立即送出所有等待中的備註。離開頁面或測試時使用。 */
  flushNotes(): Promise<void>;
}

const DEFAULT_NOTE_DEBOUNCE_MS = 600;

export function createDailyLogStore({
  writer,
  onChange,
  onError,
  noteDebounceMs = DEFAULT_NOTE_DEBOUNCE_MS
}: DailyLogStoreOptions): DailyLogStore {
  let logs: LogMap = {};

  // 以日期為 key，避免在月曆切換日期時把上一天的計時器一起取消
  const noteTimers = new Map<IsoDate, ReturnType<typeof setTimeout>>();
  const pendingNotes = new Map<IsoDate, string>();

  function entryFor(date: IsoDate): Partial<DailyLog> {
    logs[date] = logs[date] || { log_date: date };
    return logs[date];
  }

  async function send(date: IsoDate, patch: Partial<DailyLog>): Promise<void> {
    const saved = await writer.save(date, patch);

    // 以伺服器回傳的整列為準，避免本地與遠端逐漸偏離。
    //
    // 但還沒送出的備註要保留：使用者打了字、延遲儲存還沒觸發時就去勾任務，
    // 勾選的回應帶著伺服器上的「舊」備註，直接覆蓋會把正在打的字清掉。
    const pendingNote = pendingNotes.get(date);
    logs[date] = pendingNote === undefined ? saved : { ...saved, note: pendingNote };
  }

  return {
    get logs() {
      return logs;
    },

    replaceAll(next: LogMap) {
      logs = next;
    },

    async toggle(date, field, next) {
      const entry = entryFor(date);
      const previous = entry[field];

      // 先寫再送——使用者要立刻看到勾選狀態
      (entry as Record<string, unknown>)[field] = next;
      onChange();

      try {
        await send(date, { [field]: next } as Partial<DailyLog>);
        onChange();
      } catch (error) {
        // 回滾成原本的值，不是單純反轉——原本可能是 undefined
        (entryFor(date) as Record<string, unknown>)[field] = previous;
        onChange();
        onError(error instanceof Error ? error.message : "儲存失敗");
      }
    },

    setNote(date, text) {
      const entry = entryFor(date);
      entry.note = text;
      pendingNotes.set(date, text);

      clearTimeout(noteTimers.get(date));
      noteTimers.set(date, setTimeout(() => {
        void flushNote(date);
      }, noteDebounceMs));
    },

    async flushNotes() {
      const dates = [...pendingNotes.keys()];
      await Promise.all(dates.map(flushNote));
    }
  };

  async function flushNote(date: IsoDate): Promise<void> {
    const text = pendingNotes.get(date);
    if (text === undefined) return;

    clearTimeout(noteTimers.get(date));
    noteTimers.delete(date);
    pendingNotes.delete(date);

    try {
      await send(date, { note: text });
      onChange();
    } catch (error) {
      // 備註不回滾——使用者還在編輯，把輸入框的字改掉比存不起來更糟
      onError(error instanceof Error ? error.message : "備註儲存失敗");
    }
  }
}
